interface MetaTagsOptions {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  /** Canonical URL without tracking params */
  canonical?: string;
  robots?: string;
  imageAlt?: string;
}

export const updateMetaTags = (options: MetaTagsOptions) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://biltspec.com';
  const {
    title = 'Bilt:Spec | Every car has a story.',
    description = 'Every car has a story. Document your modifications, media, events, awards, and milestones in one place on Bilt:Spec.',
    image = '/images/brand/og-default.jpg',
    url = typeof window !== 'undefined' ? window.location.href : origin,
    type = 'website',
    canonical,
    robots = 'index, follow',
    imageAlt,
  } = options;

  const absoluteImage = /^https?:\/\//i.test(image) ? image : `${origin}${image.startsWith('/') ? image : `/${image}`}`;
  const canonicalUrl = canonical ?? url.split('#')[0];

  document.title = title;

  const updateMetaTag = (property: string, content: string, isProperty = false) => {
    const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
    let metaTag = document.querySelector(selector) as HTMLMetaElement;

    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (isProperty) {
        metaTag.setAttribute('property', property);
      } else {
        metaTag.setAttribute('name', property);
      }
      document.head.appendChild(metaTag);
    }

    metaTag.setAttribute('content', content);
  };

  const updateLinkTag = (rel: string, href: string) => {
    let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', rel);
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  };

  updateMetaTag('description', description);
  updateMetaTag('robots', robots);
  updateLinkTag('canonical', canonicalUrl);

  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:image', absoluteImage, true);
  updateMetaTag('og:url', canonicalUrl, true);
  updateMetaTag('og:type', type, true);
  updateMetaTag('og:site_name', 'Bilt:Spec', true);
  updateMetaTag('og:image:width', '1200', true);
  updateMetaTag('og:image:height', '630', true);
  if (imageAlt) {
    updateMetaTag('og:image:alt', imageAlt, true);
  }

  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', absoluteImage);
  updateMetaTag('twitter:url', canonicalUrl);
};

export const setNoIndexMeta = (title?: string) => {
  updateMetaTags({
    title: title ?? 'Bilt:Spec',
    robots: 'noindex, nofollow',
  });
};

export const setInvitationMetaTags = (invitationCode?: string, codeDetails?: any) => {
  const baseTitle = 'Join Bilt:Spec Beta';
  const title = invitationCode ? `${baseTitle} · ${invitationCode}` : baseTitle;

  const description = codeDetails?.description
    ? `You’ve been invited to Bilt:Spec. ${codeDetails.description} Use code ${invitationCode} to start your garage.`
    : `You’ve been invited to Bilt:Spec. Every build has a story. Use your invitation code to start your garage.`;

  updateMetaTags({
    title,
    description,
    url: window.location.href,
    robots: 'noindex, nofollow',
  });
};

export const resetDefaultMetaTags = () => {
  updateMetaTags({
    title: 'Bilt:Spec | Every car has a story.',
    description: 'Document your modifications, media, events, awards, and milestones in one place.',
    image: `${window.location.origin}/images/brand/og-default.jpg`,
    url: window.location.origin,
    canonical: window.location.origin + '/',
  });
};

/** Inject or replace JSON-LD script in document head (SPA navigation). */
export function injectJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  const id = 'bilt-json-ld';
  document.getElementById(id)?.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  const payload = Array.isArray(data)
    ? { '@context': 'https://schema.org', '@graph': data }
    : { '@context': 'https://schema.org', ...data };
  script.textContent = JSON.stringify(payload);
  document.head.appendChild(script);
}
